import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiExternalLink,
  FiRefreshCw,
  FiSearch,
} from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type {
  AdminSeoFunnelResponse,
  AdminSeoOverviewResponse,
  AdminSeoPagesResponse,
  AdminSeoQueriesResponse,
  SeoCheckCredentialsResponse,
  SeoPageCategory,
} from '@/lib/types';

const RANGE_OPTIONS = [
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];
type SeoTab = 'overview' | 'pages' | 'queries' | 'funnel';
const ALLOWED_DAYS = new Set<string>(RANGE_OPTIONS.map((r) => r.days));
const ALLOWED_TABS = new Set<SeoTab>(['overview', 'pages', 'queries', 'funnel']);

const GSC_CREDENTIAL_ENV_VARS = [
  'GOOGLE_SEARCH_CONSOLE_CREDENTIALS_JSON',
  'GOOGLE_SEARCH_CONSOLE_CREDENTIALS_JSON_B64',
] as const;

function getMissingGscEnvVars(envVars: Record<string, boolean>): string[] {
  const credentialsConfigured = GSC_CREDENTIAL_ENV_VARS.some((name) => envVars[name]);
  if (credentialsConfigured) {
    return [];
  }
  return ['GOOGLE_SEARCH_CONSOLE_CREDENTIALS_JSON or GOOGLE_SEARCH_CONSOLE_CREDENTIALS_JSON_B64'];
}

function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (Math.abs(value) >= 1000) return Math.round(value).toLocaleString();
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(2)}%`;
}

function formatPosition(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(2);
}

function formatDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>{value}</p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function formatStaleTimestamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleString(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function SeoStaleWarnings({
  gscWarning,
  gscStaleAt,
  amplitudeError,
  amplitudeStaleAt,
}: {
  gscWarning?: string | null;
  gscStaleAt?: string | null;
  amplitudeError?: string | null;
  amplitudeStaleAt?: string | null;
}) {
  const gscStaleLabel = formatStaleTimestamp(gscStaleAt);
  const amplitudeStaleLabel = formatStaleTimestamp(amplitudeStaleAt);

  return (
    <>
      {gscWarning ? (
        <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
          <div className='flex items-start gap-2'>
            <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
            <p>
              <span className='font-semibold'>Search Console data may be stale.</span>{' '}
              {gscWarning}
              {gscStaleLabel ? (
                <>
                  {' '}
                  Cached at <span className='font-mono'>{gscStaleLabel}</span>.
                </>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}
      {amplitudeError ? (
        <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
          <div className='flex items-start gap-2'>
            <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
            <p>
              {amplitudeStaleAt ? (
                <>
                  <span className='font-semibold'>On-site funnel metrics may be stale.</span>{' '}
                  {amplitudeError}
                  {amplitudeStaleLabel ? (
                    <>
                      {' '}
                      Cached at <span className='font-mono'>{amplitudeStaleLabel}</span>.
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <span className='font-semibold'>On-site funnel metrics unavailable.</span>{' '}
                  Search Console data is still shown. Amplitude export failed: {amplitudeError}
                </>
              )}
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}

function SeoConnectedBanner({ siteUrl }: { siteUrl: string }) {
  return (
    <div className='rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900'>
      <div className='flex items-start gap-2'>
        <FiCheckCircle className='mt-0.5 h-4 w-4 shrink-0' />
        <p>
          <span className='font-semibold'>Search Console connected.</span> Property:{' '}
          <span className='font-mono'>{siteUrl}</span>
        </p>
      </div>
    </div>
  );
}

function SeoCredentialsBanner({
  data,
  checkResult,
  isChecking,
  isOverviewLoading,
  overviewFetchError,
  onCheck,
  gscWarning,
  gscStaleAt,
  amplitudeError,
  amplitudeStaleAt,
}: {
  data: AdminSeoOverviewResponse | undefined;
  checkResult?: SeoCheckCredentialsResponse;
  isChecking: boolean;
  isOverviewLoading: boolean;
  overviewFetchError: Error | null;
  onCheck: () => void;
  gscWarning?: string | null;
  gscStaleAt?: string | null;
  amplitudeError?: string | null;
  amplitudeStaleAt?: string | null;
}) {
  const envVars = data?.env_vars ?? checkResult?.env_vars ?? {};
  const missing = getMissingGscEnvVars(envVars);
  const gscVerified = checkResult?.ok === true;
  const hasOverviewData = data?.ok === true;
  const awaitingResolution =
    (isOverviewLoading && !hasOverviewData) || (isChecking && checkResult == null);

  if (awaitingResolution) {
    return (
      <div className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700'>
        <div className='flex items-start gap-2'>
          <FiRefreshCw className='mt-0.5 h-4 w-4 shrink-0 animate-spin' />
          <p>
            <span className='font-semibold'>Loading SEO metrics…</span>
            {gscVerified
              ? ' Search Console credentials verified; fetching data.'
              : ' This can take up to a minute on first load.'}
          </p>
        </div>
      </div>
    );
  }

  if (hasOverviewData) {
    return (
      <div className='space-y-3'>
        <SeoConnectedBanner siteUrl={data.site_url} />
        <SeoStaleWarnings
          gscWarning={gscWarning}
          gscStaleAt={gscStaleAt}
          amplitudeError={amplitudeError}
          amplitudeStaleAt={amplitudeStaleAt}
        />
      </div>
    );
  }

  if (gscVerified) {
    const metricsError = data?.error ?? overviewFetchError?.message ?? null;
    const siteUrl = data?.site_url || checkResult.site_url;
    return (
      <div className='space-y-3'>
        <SeoConnectedBanner siteUrl={siteUrl} />
        {metricsError ? (
          <div className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700'>
            <p>
              Metrics could not be loaded: {metricsError}. Try{' '}
              <span className='font-semibold'>Refresh</span>.
            </p>
          </div>
        ) : null}
        <SeoStaleWarnings
          gscWarning={gscWarning}
          gscStaleAt={gscStaleAt}
          amplitudeError={amplitudeError}
          amplitudeStaleAt={amplitudeStaleAt}
        />
      </div>
    );
  }

  const statusMessage =
    checkResult?.error ??
    data?.error ??
    (overviewFetchError ? overviewFetchError.message : null) ??
    (missing.length > 0
      ? 'Configure Search Console credentials on the server.'
      : 'Search Console credentials could not be verified.');

  return (
    <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
      <div className='flex items-start gap-2'>
        <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
        <div className='space-y-2'>
          <p>
            <span className='font-semibold'>SEO metrics unavailable.</span> {statusMessage}
          </p>
          {missing.length > 0 ? (
            <p className='text-xs'>
              Missing env vars: <span className='font-mono'>{missing.join(', ')}</span>
            </p>
          ) : null}
          {checkResult?.credentials_env_conflict ? (
            <p className='text-xs'>
              Both credential env vars are set. Using{' '}
              <span className='font-mono'>GOOGLE_SEARCH_CONSOLE_CREDENTIALS_JSON_B64</span>. Remove
              the plain JSON var if it contains an old key.
            </p>
          ) : null}
          {checkResult?.service_account_email ? (
            <p className='text-xs'>
              Service account in use:{' '}
              <span className='font-mono'>{checkResult.service_account_email}</span>
              {checkResult.credentials_source ? (
                <>
                  {' '}
                  (from{' '}
                  <span className='font-mono'>
                    {checkResult.credentials_source === 'b64'
                      ? 'GOOGLE_SEARCH_CONSOLE_CREDENTIALS_JSON_B64'
                      : 'GOOGLE_SEARCH_CONSOLE_CREDENTIALS_JSON'}
                  </span>
                  )
                </>
              ) : null}
            </p>
          ) : null}
          {checkResult && !checkResult.ok && checkResult.accessible_sites.length > 0 ? (
            <p className='text-xs'>
              Accessible properties:{' '}
              <span className='font-mono'>{checkResult.accessible_sites.join(', ')}</span>
            </p>
          ) : null}
          <div className='flex flex-wrap items-center gap-2'>
            <button
              type='button'
              onClick={onCheck}
              disabled={isChecking}
              className='inline-flex items-center gap-1.5 rounded-md bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60'
            >
              <FiRefreshCw className={`h-3.5 w-3.5 ${isChecking ? 'animate-spin' : ''}`} />
              {isChecking ? 'Checking…' : 'Run credentials check'}
            </button>
            <a
              href='https://search.google.com/search-console'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center gap-1 text-xs font-semibold underline'
            >
              Open Search Console <FiExternalLink className='h-3 w-3' />
            </a>
            {checkResult ? (
              <span className={`text-xs ${checkResult.ok ? 'text-emerald-800' : 'text-rose-800'}`}>
                {checkResult.ok
                  ? `Connected as ${checkResult.service_account_email ?? '(service account)'}`
                  : checkResult.error ?? 'Credentials check failed'}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function DailyTrend({
  daily,
}: {
  daily: AdminSeoOverviewResponse['daily'];
}) {
  if (!daily.length) {
    return <p className='text-sm text-gray-500'>No SEO data in this date range yet.</p>;
  }
  const maxImpressions = Math.max(1, ...daily.map((d) => d.impressions));
  const maxClicks = Math.max(1, ...daily.map((d) => d.clicks));
  return (
    <div className='space-y-2'>
      <div className='flex items-center gap-4 text-xs text-gray-500'>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-sky-500' /> Clicks
        </span>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-violet-500' /> Impressions
        </span>
      </div>
      <div className='h-36 rounded border border-gray-200 bg-white p-3'>
        <div className='flex h-full items-end gap-[2px]'>
          {daily.map((d) => (
            <div key={d.date} className='group relative flex min-w-[4px] flex-1 items-end gap-[1px]'>
              <div
                className='w-1/2 rounded-t-sm bg-sky-500'
                style={{ height: `${(d.clicks / maxClicks) * 100}%` }}
                title={`${formatDateLabel(d.date)} · ${formatNumber(d.clicks)} clicks`}
              />
              <div
                className='w-1/2 rounded-t-sm bg-violet-500'
                style={{ height: `${(d.impressions / maxImpressions) * 100}%` }}
                title={`${formatDateLabel(d.date)} · ${formatNumber(d.impressions)} impressions`}
              />
            </div>
          ))}
        </div>
      </div>
      <div className='flex justify-between text-[10px] text-gray-400'>
        <span>{formatDateLabel(daily[0].date)}</span>
        <span>{formatDateLabel(daily[daily.length - 1].date)}</span>
      </div>
    </div>
  );
}

function categoryLabel(category: SeoPageCategory): string {
  switch (category) {
    case 'parenting-plan':
      return 'Parenting Plan';
    case 'child-support':
      return 'Child Support';
    default:
      return category
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
  }
}

function AdminSeoPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate({ from: '/admin/seo' });
  const { days, tab, category } = Route.useSearch();
  const [credentialCheck, setCredentialCheck] = useState<SeoCheckCredentialsResponse | undefined>(
    undefined,
  );
  const daysNum = Number(days);

  const overviewQuery = useQuery({
    queryKey: ['admin', 'seo', 'overview', daysNum],
    queryFn: () =>
      apiJson<AdminSeoOverviewResponse>(getToken, `/v1/admin/seo/overview?days=${daysNum}`),
  });
  const pagesQuery = useQuery({
    queryKey: ['admin', 'seo', 'pages', daysNum, category ?? 'all'],
    queryFn: () =>
      apiJson<AdminSeoPagesResponse>(
        getToken,
        `/v1/admin/seo/pages?days=${daysNum}${category ? `&category=${encodeURIComponent(category)}` : ''}`,
      ),
  });
  const queriesQuery = useQuery({
    queryKey: ['admin', 'seo', 'queries', daysNum],
    queryFn: () =>
      apiJson<AdminSeoQueriesResponse>(getToken, `/v1/admin/seo/queries?days=${daysNum}&limit=100`),
  });
  const funnelQuery = useQuery({
    queryKey: ['admin', 'seo', 'funnel', daysNum],
    queryFn: () =>
      apiJson<AdminSeoFunnelResponse>(getToken, `/v1/admin/seo/funnel?days=${daysNum}`),
  });

  const checkCredentialsMutation = useMutation({
    mutationFn: () =>
      apiJson<SeoCheckCredentialsResponse>(getToken, '/v1/admin/seo/check-credentials'),
    onSuccess: (data) => {
      setCredentialCheck(data);
      overviewQuery.refetch();
    },
  });

  useEffect(() => {
    checkCredentialsMutation.mutate();
    // Run once on mount to verify GSC credentials independently of overview load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTab: SeoTab = ALLOWED_TABS.has(tab as SeoTab) ? (tab as SeoTab) : 'overview';
  const overview = overviewQuery.data;
  const pages = pagesQuery.data;
  const queries = queriesQuery.data;
  const funnel = funnelQuery.data;
  const categories = useMemo(() => {
    const set = new Set<SeoPageCategory>();
    for (const row of pages?.rows ?? []) set.add(row.category);
    return Array.from(set).sort();
  }, [pages?.rows]);

  const gscWarning =
    overview?.gsc_warning ?? pages?.gsc_warning ?? queries?.gsc_warning ?? null;
  const gscStaleAt =
    overview?.gsc_stale_at ?? pages?.gsc_stale_at ?? queries?.gsc_stale_at ?? null;
  const amplitudeError =
    overview?.amplitude_error ?? pages?.amplitude_error ?? funnel?.amplitude_error ?? null;
  const amplitudeStaleAt =
    overview?.amplitude_stale_at ??
    pages?.amplitude_stale_at ??
    funnel?.amplitude_stale_at ??
    null;

  const isLoadingAny =
    overviewQuery.isLoading || pagesQuery.isLoading || queriesQuery.isLoading || funnelQuery.isLoading;

  return (
    <div className='mx-auto max-w-7xl space-y-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>SEO</h1>
          <p className='text-sm text-gray-500'>Organic search performance and on-site funnel health.</p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.days}
              type='button'
              onClick={() => navigate({ search: { days: option.days, tab: activeTab, category } })}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                option.days === days
                  ? 'border border-gray-200 bg-harbor-50 text-primary'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {option.label}
            </button>
          ))}
          <button
            type='button'
            onClick={() => {
              overviewQuery.refetch();
              pagesQuery.refetch();
              queriesQuery.refetch();
              funnelQuery.refetch();
            }}
            className='inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50'
          >
            <FiRefreshCw className='h-3.5 w-3.5' />
            Refresh
          </button>
        </div>
      </div>

      <SeoCredentialsBanner
        data={overview}
        checkResult={credentialCheck}
        isChecking={checkCredentialsMutation.isPending}
        isOverviewLoading={overviewQuery.isLoading || overviewQuery.isFetching}
        overviewFetchError={overviewQuery.error}
        onCheck={() => checkCredentialsMutation.mutate()}
        gscWarning={gscWarning}
        gscStaleAt={gscStaleAt}
        amplitudeError={amplitudeError}
        amplitudeStaleAt={amplitudeStaleAt}
      />

      <nav className='flex flex-wrap gap-2 border-b border-gray-200 pb-px'>
        {(['overview', 'pages', 'queries', 'funnel'] as SeoTab[]).map((value) => (
          <button
            key={value}
            type='button'
            onClick={() => navigate({ search: { days, tab: value, category } })}
            className={`rounded-t-md px-3 py-2 text-sm font-medium ${
              activeTab === value
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {value === 'overview'
              ? 'Overview'
              : value === 'pages'
                ? 'Pages'
                : value === 'queries'
                  ? 'Queries'
                  : 'Funnel'}
          </button>
        ))}
      </nav>

      {isLoadingAny ? (
        <div className='rounded-lg border border-gray-200 bg-white p-8'>
          <div className='mx-auto h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : null}

      {activeTab === 'overview' && overview ? (
        <div className='space-y-6'>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'>
            <StatCard label='Organic Clicks' value={formatNumber(overview.totals.clicks)} />
            <StatCard label='Impressions' value={formatNumber(overview.totals.impressions)} />
            <StatCard label='Avg CTR' value={formatPercent(overview.totals.ctr)} />
            <StatCard label='Avg Position' value={formatPosition(overview.totals.avg_position)} />
            <StatCard label='Page Views' value={formatNumber(overview.totals.page_views)} />
            <StatCard label='CTA Clicks' value={formatNumber(overview.totals.cta_clicks)} />
          </div>

          <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <p className='mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500'>
              Daily Trend
            </p>
            <DailyTrend daily={overview.daily} />
          </div>

          <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <p className='mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500'>
              Section Breakdown
            </p>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='text-xs uppercase tracking-wide text-gray-500'>
                  <tr>
                    <th className='px-3 py-2 text-left'>Category</th>
                    <th className='px-3 py-2 text-right'>Clicks</th>
                    <th className='px-3 py-2 text-right'>Impressions</th>
                    <th className='px-3 py-2 text-right'>CTR</th>
                    <th className='px-3 py-2 text-right'>Avg position</th>
                    <th className='px-3 py-2 text-right'>Page views</th>
                    <th className='px-3 py-2 text-right'>CTA clicks</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {overview.sections.map((row) => (
                    <tr key={row.category}>
                      <td className='px-3 py-2 font-medium text-gray-900'>
                        {categoryLabel(row.category)}
                      </td>
                      <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.clicks)}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>
                        {formatNumber(row.impressions)}
                      </td>
                      <td className='px-3 py-2 text-right tabular-nums'>{formatPercent(row.ctr)}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>
                        {formatPosition(row.avg_position)}
                      </td>
                      <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.page_views)}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.cta_clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === 'pages' && pages ? (
        <div className='space-y-4'>
          <div className='flex flex-wrap items-center gap-2'>
            <button
              type='button'
              onClick={() => navigate({ search: { days, tab: activeTab, category: undefined } })}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                !category ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              All categories
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type='button'
                onClick={() => navigate({ search: { days, tab: activeTab, category: cat } })}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  category === cat ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {categoryLabel(cat)}
              </button>
            ))}
          </div>
          <div className='overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm'>
            <table className='w-full text-sm'>
              <thead className='bg-gray-50 text-xs uppercase tracking-wide text-gray-500'>
                <tr>
                  <th className='px-3 py-3 text-left'>Page</th>
                  <th className='px-3 py-3 text-left'>Category</th>
                  <th className='px-3 py-3 text-right'>Clicks</th>
                  <th className='px-3 py-3 text-right'>Impressions</th>
                  <th className='px-3 py-3 text-right'>CTR</th>
                  <th className='px-3 py-3 text-right'>Avg pos</th>
                  <th className='px-3 py-3 text-right'>Page views</th>
                  <th className='px-3 py-3 text-right'>CTA clicks</th>
                  <th className='px-3 py-3 text-right'>Prerendered</th>
                  <th className='px-3 py-3 text-right'>Actions</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {pages.rows.map((row) => (
                  <tr key={row.path} className='hover:bg-gray-50'>
                    <td className='px-3 py-2 font-mono text-xs text-gray-800'>{row.path}</td>
                    <td className='px-3 py-2 text-gray-700'>{categoryLabel(row.category)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.clicks)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.impressions)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatPercent(row.ctr)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatPosition(row.avg_position)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.page_views)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.cta_clicks)}</td>
                    <td className='px-3 py-2 text-right'>
                      {row.is_prerendered ? (
                        <span className='rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800'>
                          Yes
                        </span>
                      ) : (
                        <span className='rounded bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800'>
                          No
                        </span>
                      )}
                    </td>
                    <td className='px-3 py-2 text-right'>
                      <a
                        href={`https://harborcoparent.com${row.path}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline'
                      >
                        Open <FiExternalLink className='h-3 w-3' />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {activeTab === 'queries' && queries ? (
        <div className='overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm'>
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 text-xs uppercase tracking-wide text-gray-500'>
              <tr>
                <th className='px-3 py-3 text-left'>Query</th>
                <th className='px-3 py-3 text-right'>Clicks</th>
                <th className='px-3 py-3 text-right'>Impressions</th>
                <th className='px-3 py-3 text-right'>CTR</th>
                <th className='px-3 py-3 text-right'>Avg pos</th>
                <th className='px-3 py-3 text-left'>Top page</th>
                <th className='px-3 py-3 text-right'>Opportunity</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {queries.rows.map((row) => (
                <tr key={row.query} className='hover:bg-gray-50'>
                  <td className='px-3 py-2 font-medium text-gray-900'>{row.query}</td>
                  <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.clicks)}</td>
                  <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.impressions)}</td>
                  <td className='px-3 py-2 text-right tabular-nums'>{formatPercent(row.ctr)}</td>
                  <td className='px-3 py-2 text-right tabular-nums'>{formatPosition(row.avg_position)}</td>
                  <td className='px-3 py-2 font-mono text-xs text-gray-700'>{row.top_landing_page || '—'}</td>
                  <td className='px-3 py-2 text-right'>
                    {row.is_opportunity ? (
                      <span className='inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900'>
                        <FiSearch className='h-3 w-3' /> Yes
                      </span>
                    ) : (
                      <span className='text-xs text-gray-400'>No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {activeTab === 'funnel' && funnel ? (
        <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
          <div className='mb-4 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900'>
            Signup attribution from organic search is still a proxy in v1. This view uses on-site event
            flow (page view → CTA click → sign-up screen).
          </div>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='bg-gray-50 text-xs uppercase tracking-wide text-gray-500'>
                <tr>
                  <th className='px-3 py-3 text-left'>Category</th>
                  <th className='px-3 py-3 text-right'>Page views</th>
                  <th className='px-3 py-3 text-right'>CTA clicks</th>
                  <th className='px-3 py-3 text-right'>CTA rate</th>
                  <th className='px-3 py-3 text-right'>Sign-up screens</th>
                  <th className='px-3 py-3 text-right'>Sign-up rate</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {funnel.rows.map((row) => (
                  <tr key={row.category}>
                    <td className='px-3 py-2 font-medium text-gray-900'>{categoryLabel(row.category)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.page_views)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatNumber(row.cta_clicks)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatPercent(row.cta_rate)}</td>
                    <td className='px-3 py-2 text-right tabular-nums'>
                      {formatNumber(row.sign_up_screens)}
                    </td>
                    <td className='px-3 py-2 text-right tabular-nums'>{formatPercent(row.sign_up_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {overview?.ok === false ? (
        <p className='text-xs text-gray-400'>
          Search Console can lag by 24-72 hours. Property: <span className='font-mono'>{overview.site_url}</span>
        </p>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/seo')({
  component: AdminSeoPage,
  validateSearch: (search: Record<string, unknown>) => {
    const daysRaw = typeof search.days === 'string' ? search.days : '30';
    const tabRaw = typeof search.tab === 'string' ? search.tab : 'overview';
    const categoryRaw = typeof search.category === 'string' ? search.category : undefined;
    const days = (ALLOWED_DAYS.has(daysRaw) ? daysRaw : '30') as RangeDays;
    const tab = (ALLOWED_TABS.has(tabRaw as SeoTab) ? tabRaw : 'overview') as SeoTab;
    return {
      days,
      tab,
      category: categoryRaw as SeoPageCategory | undefined,
    };
  },
});
